import { useState, useEffect, useRef } from "react";
import { uid, T, mono } from "./utils";
import { supabase } from "./supabase";

const PERSON = "ada";
const SYM = { TL: "₺", EUR: "€" };

function fmt(n, ccy) {
  const v = Math.round((n + Number.EPSILON) * 100) / 100;
  return `${SYM[ccy]}${v.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function CurrencyPanel({ ccy, advances, expenses, canEdit, canExpense, onAddAdvance, onRemoveAdvance, onAddExpense, onRemoveExpense, onOpenInvoice }) {
  const [advAmount, setAdvAmount] = useState("");
  const [advDate, setAdvDate] = useState(todayStr());
  const [expDesc, setExpDesc] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [expDate, setExpDate] = useState(todayStr());
  const [expFile, setExpFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const given = advances.reduce((s,a)=>s+Number(a.amount||0),0);
  const spent = expenses.reduce((s,x)=>s+Number(x.amount||0),0);
  const left = given - spent;
  const neg = left < 0;

  const allRows = [
    ...advances.map(a=>({type:"adv",...a})),
    ...expenses.map(x=>({type:"exp",...x})),
  ].sort((a,b)=>a.date<b.date?1:a.date>b.date?-1:0);

  const inputStyle = { background:"#fff",border:`1px solid ${T.border}`,borderRadius:2,padding:"6px 8px",fontSize:12,fontFamily:"'Inter',sans-serif",color:T.ink,outline:"none",width:"100%",boxSizing:"border-box" };

  async function handleAddExpense() {
    const amt = parseFloat(String(expAmount).replace(",","."));
    if(!expDesc.trim()||isNaN(amt)||amt<=0) return;
    setUploading(true);
    await onAddExpense({ desc: expDesc, amount: amt, date: expDate, file: expFile });
    setExpDesc(""); setExpAmount(""); setExpDate(todayStr()); setExpFile(null);
    if(fileRef.current) fileRef.current.value="";
    setUploading(false);
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:0}}>

      {/* Sol: bakiye + avans ver / Sağ: harcama listesi */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:0,flex:1,minHeight:0}}>

        {/* SOL SÜTUN — bakiye + formlar */}
        <div style={{borderRight:`1px solid ${T.border}`,padding:"24px 28px",display:"flex",flexDirection:"column",gap:20}}>

          {/* Bakiye */}
          <div>
            <div style={{display:"flex",alignItems:"baseline",gap:16,flexWrap:"wrap"}}>
              <div style={{fontSize:22,fontWeight:600,letterSpacing:"-0.02em",color:neg?"#c0392b":T.ink,lineHeight:1}}>{fmt(left,ccy)}</div>
              <div style={{display:"flex",gap:12}}>
                <span style={{...mono,fontSize:9,color:T.mute}}>Verilen <strong style={{color:"#2d7a2d"}}>{fmt(given,ccy)}</strong></span>
                <span style={{...mono,fontSize:9,color:T.mute}}>Harcanan <strong style={{color:"#c0392b"}}>{fmt(spent,ccy)}</strong></span>
              </div>
            </div>
          </div>

          {/* Avans ver */}
          {canEdit&&(
          <div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <input value={advAmount} onChange={e=>setAdvAmount(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"){const amt=parseFloat(String(advAmount).replace(",","."));if(isNaN(amt)||amt<=0)return;onAddAdvance({amount:amt,date:advDate});setAdvAmount("");setAdvDate(todayStr());}}}
                placeholder={`Avans miktarı (${SYM[ccy]})`} inputMode="decimal" style={inputStyle}/>
              <input type="date" value={advDate} onChange={e=>setAdvDate(e.target.value)} style={{...inputStyle,color:T.mute}}/>
              <button onClick={()=>{const amt=parseFloat(String(advAmount).replace(",","."));if(isNaN(amt)||amt<=0)return;onAddAdvance({amount:amt,date:advDate});setAdvAmount("");setAdvDate(todayStr());}}
                style={{background:"#2d7a2d",color:"#fff",border:"none",borderRadius:2,padding:"7px",fontSize:12,fontWeight:500,cursor:"pointer"}}>Avans Ver</button>
            </div>
          </div>
          )}

          {/* Harcama ekle */}
          {canExpense&&(
          <div style={{borderTop:`1px solid ${T.borderLight}`,paddingTop:18}}>
            <div style={{...mono,fontSize:9,color:"#c0392b",marginBottom:8,letterSpacing:"0.1em"}}>− HARCAMA EKLE</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <input value={expDesc} onChange={e=>setExpDesc(e.target.value)} placeholder="Açıklama" style={inputStyle}/>
              <input value={expAmount} onChange={e=>setExpAmount(e.target.value)} placeholder={`Miktar (${SYM[ccy]})`} inputMode="decimal" style={inputStyle}/>
              <input type="date" value={expDate} onChange={e=>setExpDate(e.target.value)} style={{...inputStyle,color:T.mute}}/>
              <label style={{...mono,fontSize:8,color:T.mute,cursor:"pointer",border:`1px dashed ${T.border}`,borderRadius:2,padding:"5px 8px",textAlign:"center"}}>
                {expFile?`📎 ${expFile.name.slice(0,24)}`:"📎 Fatura ekle (opsiyonel)"}
                <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={e=>setExpFile(e.target.files?.[0]||null)} style={{display:"none"}}/>
              </label>
              {expFile&&<button onClick={()=>{setExpFile(null);if(fileRef.current)fileRef.current.value="";}} style={{...mono,fontSize:8,background:"none",border:"none",color:"#c0392b",cursor:"pointer",padding:0}}>faturayı kaldır</button>}
              <button onClick={handleAddExpense} disabled={uploading}
                style={{background:"#c0392b",color:"#fff",border:"none",borderRadius:2,padding:"7px",fontSize:12,fontWeight:500,cursor:uploading?"wait":"pointer",opacity:uploading?0.6:1}}>
                {uploading?"Yükleniyor…":"Ekle"}
              </button>
            </div>
          </div>
          )}
        </div>

        {/* SAĞ SÜTUN — harcama listesi */}
        <div style={{padding:"24px 28px",overflowY:"auto"}}>
          <div style={{...mono,fontSize:9,color:T.mute,marginBottom:12,letterSpacing:"0.12em"}}>HARCAMA LİSTESİ</div>
          {allRows.length===0
            ? <div style={{...mono,fontSize:9,color:T.border}}>Henüz kayıt yok.</div>
            : allRows.map((r,i)=>(
              <div key={r.type+r.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0",borderBottom:`1px solid ${T.borderLight}`}}>
                <span style={{...mono,fontSize:7,padding:"2px 5px",borderRadius:2,flexShrink:0,background:r.type==="adv"?"#e7f3e7":"#fbe9e7",color:r.type==="adv"?"#2d7a2d":"#c0392b"}}>{r.type==="adv"?"AVANS":"GİDER"}</span>
                <span style={{flex:1,fontSize:12,color:T.ink,minWidth:0,overflowWrap:"break-word",lineHeight:1.3}}>{r.type==="adv"?"Avans ödemesi":r.description}</span>
                {r.type==="exp"&&r.invoice_path&&(
                  <button onClick={()=>onOpenInvoice(r.invoice_path)} style={{...mono,fontSize:7,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"2px 5px",cursor:"pointer",color:T.mute,flexShrink:0}}>fatura ↗</button>
                )}
                <span style={{...mono,fontSize:8,color:T.mute,flexShrink:0}}>{r.date?.slice(5)}</span>
                <span style={{...mono,fontSize:11,fontWeight:600,flexShrink:0,color:r.type==="adv"?"#2d7a2d":"#c0392b"}}>{r.type==="adv"?"+":"−"}{fmt(Number(r.amount),ccy)}</span>
                {(r.type==="adv"?canEdit:canExpense)&&(
                  <button onClick={()=>r.type==="adv"?onRemoveAdvance(r.id):onRemoveExpense(r)}
                    style={{background:"none",border:"none",color:T.mute,fontSize:14,cursor:"pointer",padding:0,flexShrink:0,opacity:0.4}}
                    onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.4"}>×</button>
                )}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}

export function AvansView({ accessLevel }) {
  const canEdit = accessLevel === "full";
  const canExpense = true; // herkes harcama girebilir
  const [advances, setAdvances] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEUR, setShowEUR] = useState(false);

  useEffect(()=>{ loadAll(); },[]); // eslint-disable-line

  async function loadAll(){
    setLoading(true);
    const { data: adv } = await supabase.from("avans_advances").select("*").eq("person",PERSON).order("date");
    const { data: exp } = await supabase.from("avans_expenses").select("*").eq("person",PERSON).order("date");
    setAdvances(adv||[]);
    setExpenses(exp||[]);
    setLoading(false);
  }

  async function addAdvance(ccy, { amount, date }) {
    if(isNaN(amount)||amount<=0) return;
    const row = { id:uid(), person:PERSON, amount, currency:ccy, date };
    setAdvances(p=>[...p,row]);
    const { error } = await supabase.from("avans_advances").insert(row);
    if(error){ console.error(error); loadAll(); }
  }
  async function removeAdvance(id){
    if(!window.confirm("Bu avansı silmek istediğine emin misin?")) return;
    setAdvances(p=>p.filter(a=>a.id!==id));
    await supabase.from("avans_advances").delete().eq("id",id);
  }
  async function addExpense(ccy, { desc, amount, date, file }) {
    let invoice_path = "";
    if(file){
      const path = `${PERSON}/${uid()}-${file.name.replace(/[^a-zA-Z0-9.\-_]/g,"_")}`;
      const { error: upErr } = await supabase.storage.from("avans-faturalar").upload(path, file);
      if(upErr) console.error("Fatura yüklenemedi:", upErr);
      else invoice_path = path;
    }
    const row = { id:uid(), person:PERSON, description:desc, amount, currency:ccy, date, invoice_path };
    setExpenses(p=>[...p,row]);
    const { error } = await supabase.from("avans_expenses").insert(row);
    if(error){ console.error(error); loadAll(); }
  }
  async function removeExpense(x){
    if(!window.confirm("Bu harcamayı silmek istediğine emin misin?")) return;
    setExpenses(p=>p.filter(e=>e.id!==x.id));
    if(x.invoice_path) await supabase.storage.from("avans-faturalar").remove([x.invoice_path]);
    await supabase.from("avans_expenses").delete().eq("id",x.id);
  }
  function openInvoice(path){
    const { data } = supabase.storage.from("avans-faturalar").getPublicUrl(path);
    if(!data?.publicUrl){ alert("Fatura açılamadı."); return; }
    window.open(data.publicUrl, "_blank");
  }

  if(loading) return <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{...mono,color:T.mute}}>Loading</span></div>;

  const hasEUR = advances.some(a=>a.currency==="EUR")||expenses.some(x=>x.currency==="EUR");
  const eurVisible = hasEUR || showEUR;

  return (
    <div style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>
      <div style={{maxWidth:780,margin:"0 auto"}}>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
          {canEdit&&!eurVisible&&(
            <button onClick={()=>setShowEUR(true)} style={{...mono,fontSize:9,background:"none",border:`1px solid ${T.border}`,borderRadius:2,padding:"5px 10px",cursor:"pointer",color:T.mute}}>+ EUR ekle</button>
          )}
        </div>
        <div style={{display:"grid",gridTemplateColumns:eurVisible?"1fr 1fr":"1fr",gap:32}}>
          {/* EUR sütunu */}
          {eurVisible&&(
          <div>
            <div style={{...mono,fontSize:11,fontWeight:600,color:T.ink,marginBottom:14,letterSpacing:"0.1em",paddingBottom:8,borderBottom:`2px solid ${T.ink}`}}>EUR €</div>
            <CurrencyPanel
              ccy="EUR"
              canEdit={canEdit}
              canExpense={canExpense}
              advances={advances.filter(a=>a.currency==="EUR")}
              expenses={expenses.filter(x=>x.currency==="EUR")}
              onAddAdvance={p=>addAdvance("EUR",p)}
              onRemoveAdvance={removeAdvance}
              onAddExpense={p=>addExpense("EUR",p)}
              onRemoveExpense={removeExpense}
              onOpenInvoice={openInvoice}
            />
          </div>
          )}
          {/* TL sütunu */}
          <div>
            <div style={{...mono,fontSize:11,fontWeight:600,color:T.ink,marginBottom:14,letterSpacing:"0.1em",paddingBottom:8,borderBottom:`2px solid ${T.ink}`}}>TL ₺</div>
            <CurrencyPanel
              ccy="TL"
              canEdit={canEdit}
              canExpense={canExpense}
              advances={advances.filter(a=>a.currency==="TL")}
              expenses={expenses.filter(x=>x.currency==="TL")}
              onAddAdvance={p=>addAdvance("TL",p)}
              onRemoveAdvance={removeAdvance}
              onAddExpense={p=>addExpense("TL",p)}
              onRemoveExpense={removeExpense}
              onOpenInvoice={openInvoice}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
